"use client";

import { makeFunctionReference } from "convex/server";
import { useMutation } from "convex/react";
import { ExternalLink, FileBarChart } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const createShareLink = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    customerId: string;
    customerName?: string;
    actor: "dispatcher";
    actorId?: string;
  },
  { status: "drafted" | "blocked"; urlPath?: string; approvalId?: string }
>("customerPerformanceReports:createShareLink");

export function CustomerPerformanceReportPanel({
  tenantId,
  customerId,
  customerName,
}: {
  tenantId: string;
  customerId: string;
  customerName: string;
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <CustomerPerformanceReportContent customerName={customerName} />;
  }
  return <CustomerPerformanceReportLive tenantId={tenantId} customerId={customerId} customerName={customerName} />;
}

function CustomerPerformanceReportLive(props: { tenantId: string; customerId: string; customerName: string }) {
  const create = useMutation(createShareLink);
  return (
    <CustomerPerformanceReportContent
      customerName={props.customerName}
      onCreate={() =>
        create({
          tenantId: props.tenantId,
          customerId: props.customerId,
          customerName: props.customerName,
          actor: "dispatcher",
          actorId: "current-user",
        })
      }
    />
  );
}

function CustomerPerformanceReportContent({
  customerName,
  onCreate,
}: {
  customerName: string;
  onCreate?: () => Promise<{ status: "drafted" | "blocked"; urlPath?: string; approvalId?: string }>;
}) {
  const [result, setResult] = useState<{ status: string; detail: string; urlPath?: string }>({
    status: "idle",
    detail: "No customer-facing report staged",
  });
  const [isCreating, setIsCreating] = useState(false);

  async function createReport() {
    setIsCreating(true);
    try {
      const response = onCreate
        ? await onCreate()
        : {
            status: "drafted" as const,
            urlPath: "/reports/customer/demo-scorecard",
            approvalId: "approval_demo",
          };
      setResult({
        status: response.status,
        detail:
          response.status === "blocked"
            ? "Blocked by customer SOP"
            : `Drafted for reviewer approval${response.approvalId ? ` (${response.approvalId})` : ""}`,
        urlPath: response.urlPath,
      });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3 text-base">
          <span className="flex items-center gap-2">
            <FileBarChart size={16} />
            Share Report
          </span>
          <Badge variant={result.status === "blocked" ? "bad" : result.status === "drafted" ? "warn" : "info"}>
            {result.status}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium">{customerName}</div>
            <div className="text-muted-foreground">{result.detail}</div>
          </div>
          <Button variant="secondary" onClick={createReport} disabled={isCreating}>
            <ExternalLink size={14} />
            Stage Link
          </Button>
        </div>
        {result.urlPath ? <div className="rounded-md border border-border p-2 text-xs text-muted-foreground">{result.urlPath}</div> : null}
      </CardContent>
    </Card>
  );
}
